import { PermissionAction, PermissionApiResponse, PermissionApiResponseItem, SITE_SETTINGS_FALLBACK } from '@weavix/models/src/permission/permissions.model';
import { PersonFacilityPredicate, PersonLike } from '@weavix/models/src/person/person-matcher';
import { isPackageEntitled } from '../packaging/package-gated-actions';
import { ProductPackageEntitlements } from '../packaging/resolve-product-packages';
import { matchPerson } from './match-person';

/** A site's product-package entitlements, or the whole account's without a site; null means `enabledActions` decides. */
export type SiteEntitlementsProvider = (facilityId?: string) => ProductPackageEntitlements | null;

const entitlementIds = new WeakMap<ProductPackageEntitlements, number>();
let nextEntitlementId = 1;

/**
 * Identifies the entitlements a check reads so memoized answers follow them, and calls `onChange` when a site's
 * entitlements change so superseded answers can be dropped. Reading here, outside a memo, lets mobx reactions track them.
 */
export class EntitlementKeys {
    private readonly lastIds = new Map<string, number>();

    constructor(private readonly provider: SiteEntitlementsProvider | undefined, private readonly onChange: () => void) {}

    /** A key for the entitlements at `sites` (undefined meaning the whole account); empty without a provider. */
    key(sites: ReadonlyArray<string | undefined>): string {
        if (!this.provider) return '';
        return sites.map(site => {
            const id = entitlementId(this.provider?.(site));
            const key = site ?? '';
            if (this.lastIds.has(key) && this.lastIds.get(key) !== id) this.onChange();
            this.lastIds.set(key, id);
            return id;
        }).join(',');
    }
}

/** A stable number per entitlements object, 0 for none. */
function entitlementId(entitlements: ProductPackageEntitlements | null | undefined): number {
    if (!entitlements) return 0;
    let id = entitlementIds.get(entitlements);
    if (!id) {
        id = nextEntitlementId++;
        entitlementIds.set(entitlements, id);
    }
    return id;
}

/** True only when resolved product packages leave `action` out; otherwise nothing changes from before. */
export function isPackageDenied(action: PermissionAction, entitlements: ProductPackageEntitlements | null | undefined): boolean {
    return isPackageEntitled(action, entitlements) === false;
}

/**
 * Whether the account is entitled to `action`: resolved product packages decide catalog-gated actions; otherwise
 * `enabledActions`, letting a site-settings action stand in for a split one, as on the server.
 */
export function isEntitledAction(action: PermissionAction, enabledActions: ReadonlyArray<PermissionAction>, entitlements: ProductPackageEntitlements | null | undefined): boolean {
    const licensed = isPackageEntitled(action, entitlements);
    if (licensed !== undefined) return licensed;
    const fallback = SITE_SETTINGS_FALLBACK[action];
    return enabledActions.includes(action) || (!!fallback && enabledActions.includes(fallback));
}

/** Whether `grantedAction` covers `desiredAction`: it, its site-settings fallback, or Administer Account. */
export function grantCovers(desiredAction: PermissionAction, grantedAction: PermissionAction): boolean {
    const fallback = SITE_SETTINGS_FALLBACK[desiredAction];
    return grantedAction === PermissionAction.AdministerAccount || grantedAction === desiredAction || (!!fallback && grantedAction === fallback);
}

/** Whether `grant` lets the user see `person` in the directory or Live Look at `facilityId`. */
export function grantCoversPerson(
    grant: PermissionApiResponseItem,
    action: PermissionAction,
    person: PersonLike,
    permissions: PermissionApiResponse,
    isPersonOnFacility?: PersonFacilityPredicate,
    facilityId?: string,
): boolean {
    if (facilityId && grant.scope.facilityId && grant.scope.facilityId !== facilityId) return false;
    if (!grant.grantedActions.includes(action) && !grant.grantedActions.includes(PermissionAction.AdministerAccount)) return false;
    return action === PermissionAction.ViewDirectory && (!grant.directory || matchPerson(grant.directory, person, isPersonOnFacility, permissions.self))
        || action === PermissionAction.ViewWorkerLiveLook && (!grant.livelook || matchPerson(grant.livelook, person, isPersonOnFacility, permissions.self));
}
